/**
 * Single source of truth for the flow-editor palette.
 *
 * Each `FlowEditorDragType` maps to:
 *  - one or more lowercase step titles that resolve to it (the editor matches
 *    step types by `stepType.title.toLowerCase()`)
 *  - a palette category, which decides the section it renders in
 *
 * Adding a new step type? Add a `FlowEditorDragType` enum value AND an entry
 * to `STEP_TYPE_REGISTRY`. The `Record<FlowEditorDragType, ...>` typing makes
 * a missing entry a typecheck failure — no more silent palette drops.
 */

/**
 * Enum for drag-and-drop step types in the flow editor.
 *
 * Each value MUST have a matching entry in `STEP_TYPE_REGISTRY` below.
 */
export enum FlowEditorDragType {
  AI_RETURN_REASONS = "AI_RETURN_REASONS",
  CONDITION = "CONDITION",
  MULTIPLE_CHOICE = "MULTIPLE_CHOICE",
  RETURN_REASON = "RETURN_REASON",
  INPUT = "INPUT",
  ORDER_DATE_INPUT = "ORDER_DATE_INPUT",
  INFORMATION = "INFORMATION",
  AB_TEST = "AB_TEST",
  WAIT = "WAIT",
  ISSUE_COMPENSATION = "ISSUE_COMPENSATION",
  RESTOCKING = "RESTOCKING",
  END = "END",

  REJECT = "REJECT",
  ADD_RETURN_TAG = "ADD_RETURN_TAG",
  ADD_EXCHANGE_ORDER_TAGS = "ADD_EXCHANGE_ORDER_TAGS",
  BLOCK = "BLOCK",
  RETURN = "RETURN",
  FLAG = "FLAG",
  MANUAL_REVIEW = "MANUAL_REVIEW",
  MERCHANT_REVIEW_BEFORE_FILING = "MERCHANT_REVIEW_BEFORE_FILING",
  OVERRIDE_ADJUSTMENT = "OVERRIDE_ADJUSTMENT",
  OVERRIDE_GREEN_RETURN = "OVERRIDE_GREEN_RETURN",
  DYNAMIC_BUNDLE_ADJUSTMENT = "DYNAMIC_BUNDLE_ADJUSTMENT",
  EXCHANGE_SHIPPING_NAME = "EXCHANGE_SHIPPING_NAME",
  EXCHANGE_SHIPPING_RATES = "EXCHANGE_SHIPPING_RATES",
  DISCOUNT_CODE_NAME = "DISCOUNT_CODE_NAME",
  SUBMIT_CLAIM = "SUBMIT_CLAIM",
  SUBMIT_REPAIR = "SUBMIT_REPAIR",
  SUBMIT_WARRANTY = "SUBMIT_WARRANTY",
  SUBMIT_WARRANTY_REGISTRATION = "SUBMIT_WARRANTY_REGISTRATION",
  SET_RETURN_LOCATIONS = "SET_RETURN_LOCATIONS",
  REDO_MANAGED_LOCATION = "REDO_MANAGED_LOCATION",
  DONATE = "DONATE",
  COMBINE_LABEL = "COMBINE_LABEL",
  SEPARATE_LABELS = "SEPARATE_LABELS",
  RETURN_PROCESSING_DECISION = "RETURN_PROCESSING_DECISION",
  EVIDENCE = "EVIDENCE",
  AGENT_VERIFICATION = "AGENT_VERIFICATION",
  ASSIGN_GRADE = "ASSIGN_GRADE",
  MAKE_SERVICE_REQUEST = "MAKE_SERVICE_REQUEST",
  TRANSFORM_PRODUCT = "TRANSFORM_PRODUCT",
}

export type StepCategoryKey = "steps" | "actions" | "serviceRequests";

export interface StepTypeRegistryEntry {
  /** Lowercase step titles that resolve to this drag type. */
  readonly titles: readonly string[];
  /** Which palette section to render this drag type in. */
  readonly category: StepCategoryKey;
}

/**
 * Authoritative drag type ↔ title(s) + category map. Both
 * `stepTypeToDragType` (lookup by title) and `handleStepDropped`
 * (lookup by drag type) derive their behavior from this single table.
 *
 * Typed as `Record<FlowEditorDragType, ...>` so adding an enum value
 * without a matching entry fails typecheck.
 */
export const STEP_TYPE_REGISTRY: Record<
  FlowEditorDragType,
  StepTypeRegistryEntry
> = {
  [FlowEditorDragType.AI_RETURN_REASONS]: {
    titles: ["ai return reasons", "ai warranty reasons"],
    category: "steps",
  },
  [FlowEditorDragType.CONDITION]: { titles: ["condition"], category: "steps" },
  [FlowEditorDragType.MULTIPLE_CHOICE]: {
    titles: ["multiple choice"],
    category: "steps",
  },
  [FlowEditorDragType.RETURN_REASON]: {
    titles: ["return reason", "claim reason", "warranty reason"],
    category: "steps",
  },
  [FlowEditorDragType.INPUT]: { titles: ["input"], category: "steps" },
  [FlowEditorDragType.ORDER_DATE_INPUT]: {
    titles: ["order date"],
    category: "steps",
  },
  [FlowEditorDragType.INFORMATION]: {
    titles: ["information"],
    category: "steps",
  },
  [FlowEditorDragType.AB_TEST]: { titles: ["a/b test"], category: "steps" },
  [FlowEditorDragType.RETURN_PROCESSING_DECISION]: {
    titles: ["decision"],
    category: "steps",
  },
  [FlowEditorDragType.EVIDENCE]: {
    titles: ["capture evidence"],
    category: "steps",
  },
  [FlowEditorDragType.AGENT_VERIFICATION]: {
    titles: ["agent verification"],
    category: "steps",
  },
  [FlowEditorDragType.WAIT]: { titles: ["wait"], category: "actions" },
  [FlowEditorDragType.ISSUE_COMPENSATION]: {
    titles: ["issue compensation"],
    category: "actions",
  },
  [FlowEditorDragType.RESTOCKING]: {
    titles: ["restocking"],
    category: "actions",
  },
  [FlowEditorDragType.END]: { titles: ["end"], category: "actions" },
  [FlowEditorDragType.REJECT]: { titles: ["reject"], category: "actions" },
  [FlowEditorDragType.ADD_RETURN_TAG]: {
    titles: ["add return tag", "add claim tag", "add warranty tag"],
    category: "actions",
  },
  [FlowEditorDragType.ADD_EXCHANGE_ORDER_TAGS]: {
    titles: ["add exchange order tags"],
    category: "actions",
  },
  [FlowEditorDragType.BLOCK]: { titles: ["block"], category: "actions" },
  [FlowEditorDragType.RETURN]: {
    titles: ["return", "submit advanced claim", "submit advanced warranty"],
    category: "actions",
  },
  [FlowEditorDragType.FLAG]: { titles: ["flag"], category: "actions" },
  [FlowEditorDragType.MANUAL_REVIEW]: {
    titles: ["mark for manual review"],
    category: "actions",
  },
  [FlowEditorDragType.MERCHANT_REVIEW_BEFORE_FILING]: {
    titles: ["review before filing claim"],
    category: "actions",
  },
  [FlowEditorDragType.OVERRIDE_ADJUSTMENT]: {
    titles: ["override adjustment"],
    category: "actions",
  },
  [FlowEditorDragType.OVERRIDE_GREEN_RETURN]: {
    titles: ["require send back"],
    category: "actions",
  },
  [FlowEditorDragType.DYNAMIC_BUNDLE_ADJUSTMENT]: {
    titles: ["dynamic bundle adjustment"],
    category: "actions",
  },
  [FlowEditorDragType.EXCHANGE_SHIPPING_NAME]: {
    titles: ["shipping name"],
    category: "actions",
  },
  [FlowEditorDragType.EXCHANGE_SHIPPING_RATES]: {
    titles: ["exchange shipping rates"],
    category: "actions",
  },
  [FlowEditorDragType.DISCOUNT_CODE_NAME]: {
    titles: ["discount code name"],
    category: "actions",
  },
  [FlowEditorDragType.SUBMIT_CLAIM]: {
    titles: ["submit claim"],
    category: "actions",
  },
  [FlowEditorDragType.SUBMIT_REPAIR]: {
    titles: ["submit repair"],
    category: "actions",
  },
  [FlowEditorDragType.SUBMIT_WARRANTY]: {
    titles: ["submit warranty"],
    category: "actions",
  },
  [FlowEditorDragType.SUBMIT_WARRANTY_REGISTRATION]: {
    titles: ["submit warranty registration"],
    category: "actions",
  },
  [FlowEditorDragType.SET_RETURN_LOCATIONS]: {
    titles: ["location"],
    category: "actions",
  },
  [FlowEditorDragType.REDO_MANAGED_LOCATION]: {
    titles: ["returns center", "redo returns center"],
    category: "actions",
  },
  [FlowEditorDragType.DONATE]: { titles: ["donate"], category: "actions" },
  [FlowEditorDragType.COMBINE_LABEL]: {
    titles: ["combine label"],
    category: "actions",
  },
  [FlowEditorDragType.SEPARATE_LABELS]: {
    titles: ["separate labels"],
    category: "actions",
  },
  [FlowEditorDragType.ASSIGN_GRADE]: {
    titles: ["assign grade"],
    category: "actions",
  },
  [FlowEditorDragType.MAKE_SERVICE_REQUEST]: {
    titles: ["make service request"],
    category: "serviceRequests",
  },
  [FlowEditorDragType.TRANSFORM_PRODUCT]: {
    titles: ["transform product"],
    category: "actions",
  },
};

/**
 * Look up a `FlowEditorDragType` for a step's title. Returns undefined
 * when the title doesn't match any registered entry (e.g., the synthetic
 * TRIGGER or PHASE_ANCHOR steps that don't appear in the palette).
 */
export function dragTypeFromTitle(
  title: string,
): FlowEditorDragType | undefined {
  const lower = title.toLowerCase();
  for (const [dragType, entry] of Object.entries(STEP_TYPE_REGISTRY)) {
    if (entry.titles.includes(lower)) {
      return dragType as FlowEditorDragType;
    }
  }
  return undefined;
}

/** True when `title` is one of the lowercase strings registered for `dragType`. */
export function dragTypeMatchesTitle(
  dragType: FlowEditorDragType,
  title: string,
): boolean {
  return STEP_TYPE_REGISTRY[dragType].titles.includes(title.toLowerCase());
}

/** Palette section for a given drag type. */
export function categoryOf(dragType: FlowEditorDragType): StepCategoryKey {
  return STEP_TYPE_REGISTRY[dragType].category;
}
